<template>
  <div id="data-list-thumb-view" class="data-list-container diagnostic-log">
    <div class="vx-card p-4">
      <h4 class="mb-1">Kasa Teşhis Günlüğü</h4>
      <p class="text-sm text-secondary2 mb-4">
        "Sorun oldu" şikâyetlerinde şube, personel ve saat aralığı seçerek kasada olan her şeyi sırasıyla görün:
        POS istek/sonuçları, ekran kilidi, sayfa yenileme, sipariş kapatma hataları ve uygulama hataları.
      </p>

      <div v-if="!isSuperAdmin" class="text-danger">Bu sayfayı yalnızca süper admin görüntüleyebilir.</div>

      <template v-else>
        <vs-row vs-type="flex" vs-w="12" class="bg-white p-4 mb-4 border border-solid d-theme-border-grey-light rounded flex-wrap items-end">
          <vs-col vs-w="2" vs-sm="6" class="px-1 py-1">
            <span class="text-secondary2 text-xsmall ml-1 weight-600">Başlangıç</span>
            <flat-pickr v-model="dateFrom" class="w-full vs-inputx vs-input--input normal" :config="dateTimeConfig" />
          </vs-col>
          <vs-col vs-w="2" vs-sm="6" class="px-1 py-1">
            <span class="text-secondary2 text-xsmall ml-1 weight-600">Bitiş</span>
            <flat-pickr v-model="dateTo" class="w-full vs-inputx vs-input--input normal" :config="dateTimeConfig" />
          </vs-col>
          <vs-col vs-w="2" vs-sm="12" class="px-1 py-1">
            <span class="text-secondary2 text-xsmall ml-1 weight-600">Şube</span>
            <v-select v-model="selectedBranchId" :options="branches" label="name" :reduce="(b) => b.id"
                      placeholder="Tüm şubeler" class="w-full" :clearable="true" @input="onBranchChange" />
          </vs-col>
          <vs-col vs-w="2" vs-sm="12" class="px-1 py-1">
            <span class="text-secondary2 text-xsmall ml-1 weight-600">Personel (PIN)</span>
            <v-select v-model="selectedPersonalId" :options="personnels" label="name" :reduce="(p) => p.id"
                      placeholder="Tümü" class="w-full" :clearable="true" />
          </vs-col>
          <vs-col vs-w="2" vs-sm="12" class="px-1 py-1">
            <span class="text-secondary2 text-xsmall ml-1 weight-600">Olay Türü</span>
            <v-select v-model="selectedEventType" :options="eventTypeOptions" label="label" :reduce="(o) => o.value"
                      placeholder="Tümü" class="w-full" :clearable="true" />
          </vs-col>
          <vs-col vs-w="2" vs-sm="12" class="px-1 py-1">
            <span class="text-secondary2 text-xsmall ml-1 weight-600">Oturum ID</span>
            <input v-model="filterClientSessionId" class="w-full vs-inputx vs-input--input normal" placeholder="Oturum ID..." />
          </vs-col>
          <vs-col vs-w="12" vs-type="flex" vs-justify="space-between" vs-align="center" class="px-1 pt-3">
            <vs-checkbox v-model="onlyProblems">Sadece sorunlu olaylar</vs-checkbox>
            <div>
              <vs-button @click="resetFilters" type="border" size="small" color="warning" class="mr-2">Temizle</vs-button>
              <vs-button @click="loadEvents" icon-pack="feather" icon="icon-search" size="small" color="primary">Ara</vs-button>
            </div>
          </vs-col>
        </vs-row>

        <div class="flex justify-between items-center mb-2 px-1">
          <span class="text-sm font-medium">
            {{ visibleItems.length }} olay gösteriliyor<span v-if="totalCount"> (toplam {{ totalCount }})</span>
          </span>
          <span v-if="isTruncated" class="text-sm text-warning">
            Sonuç çok fazla, ilk {{ items.length }} olay gösteriliyor — saat aralığını veya şubeyi daraltın.
          </span>
        </div>

        <div class="diag-table-wrap">
          <table class="diag-table">
            <thead>
              <tr>
                <th>Kasa Saati</th>
                <th>Şube</th>
                <th>Personel (PIN)</th>
                <th>Kasa Hesabı</th>
                <th>Oturum</th>
                <th>Olay</th>
                <th>Detay</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!isLoading && visibleItems.length === 0">
                <td colspan="7" class="text-center p-4 text-secondary2">Kayıt bulunamadı.</td>
              </tr>
              <tr v-for="row in visibleItems" :key="row.id" :class="rowClass(row)">
                <td class="nowrap">
                  <div>{{ formatDateTime(eventTime(row)) }}</div>
                  <div v-if="serverDelaySeconds(row) > 5" class="text-xs text-warning"
                       :title="'Sunucuya ulaşma: ' + formatDateTime(row.created)">
                    sunucuya {{ serverDelaySeconds(row) }} sn gecikmeli ulaştı
                  </div>
                </td>
                <td>{{ row.branchName || ('#' + row.branchId) }}</td>
                <td>{{ row.personalFullName || (row.personalId ? '#' + row.personalId : '—') }}</td>
                <td>{{ row.userFullName || ('#' + row.userId) }}</td>
                <td class="nowrap">
                  <a v-if="row.clientSessionId" href="#" :title="'Bu oturumun tüm olayları: ' + row.clientSessionId"
                     @click.prevent="filterBySession(row.clientSessionId)">
                    {{ row.clientSessionId.split('-').pop() }}
                  </a>
                  <span v-else>—</span>
                </td>
                <td><span class="diag-badge" :class="badgeClass(row)">{{ eventLabel(row) }}</span></td>
                <td class="diag-detail">
                  <div v-for="(line, i) in detailLines(row)" :key="i">{{ line }}</div>
                  <details v-if="row.payloadJson" class="mt-1">
                    <summary class="text-xs cursor-pointer text-secondary2">Ham veri</summary>
                    <pre class="diag-raw">{{ prettyJson(row.payloadJson) }}</pre>
                  </details>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { getSelfServiceDiagnosticEvents } from "@/api/SelfServiceSessionLog";
import { getBranchNameList, getPersonalNameList } from "@/api/User";

/** Sorun göstergesi sayılan olaylar (kırmızı satır + "Sadece sorunlu olaylar" filtresi).
 * "Ödeme akışı iptal" çoğunlukla kasiyerin ödeme ekranından geri dönmesidir; sorun sayılmaz.
 * POS'un kartı reddetmesi ve otomatik yeniden deneme adımları da normal akıştır (bkz. isProblem). */
const PROBLEM_TYPES = [
  "PosSaleFailed",
  "PosPaymentManuallyConfirmed",
  "OrderCloseFailed",
  "ClientError",
  "PosConnectFailed",
  "PriceValidationBlocked"
];
const NON_PROBLEM_POS_OUTCOMES = ["declined", "pendingDeclined", "busyAutoRetry"];

const PAYLOAD_LABELS = {
  amount: "Tutar",
  paymentMethodName: "Ödeme yöntemi",
  cartTotal: "Sepet toplamı",
  cartId: "Sepet",
  bridge: "POS köprüsü",
  posPaymentType: "POS ödeme tipi",
  itemCount: "Satır sayısı",
  outcome: "Sonuç",
  reason: "Neden",
  message: "Mesaj",
  statusId: "POS durum kodu",
  statusCode: "POS durum kodu",
  orderNo: "POS sipariş no",
  saleNumber: "POS satış no",
  wasPending: "Askıya düştü",
  askedCashier: "Kasiyere soruldu",
  durationMs: "Süre",
  httpStatus: "HTTP durumu",
  paymentsCount: "Ödeme sayısı",
  paidTotal: "Ödenen toplam",
  totalPrice: "Sipariş tutarı",
  trigger: "Tetikleyen",
  applied: "Uygulandı",
  skippedBecause: "Uygulanmadı çünkü",
  navigation: "Açılış türü",
  appVersion: "Uygulama sürümü",
  updatedFrom: "Otomatik güncellendi (önceki sürüm)",
  online: "İnternet",
  name: "Personel",
  locked: "Kilitli",
  source: "Kaynak",
  file: "Dosya",
  line: "Satır",
  page: "Sayfa",
  device: "Cihaz",
  autoRetry: "Otomatik yeniden deneme",
  retryAttempt: "Deneme no",
  autoRetryBlocked: "Otomatik deneme yapılmadı (önceki ödemenin sonucu belirsiz)",
  errors: "Doğrulama Hataları",
  cartEntries: "Sepet Satırları",
  priceValidationErrors: "Fiyat Doğrulama Hataları (POS Sonrası)"
};

const VALUE_LABELS = {
  outcome: {
    unknown: "Sonuç alınamadı",
    cashierConfirmedPaid: "Kasiyer: ödeme alındı",
    cashierConfirmedNotPaid: "Kasiyer: ödeme alınmadı",
    declined: "POS reddetti / iptal",
    pendingDeclined: "Askıdan sonra reddedildi",
    hasError: "POS hata döndü",
    busyAutoRetry: "POS meşgul / fiş basıyor — otomatik tekrar deneniyor"
  },
  reason: {
    timeout: "Zaman aşımı",
    network: "Köprüye ulaşılamadı",
    bridgeHttpError: "Köprü hata döndü",
    pendingUnresolved: "Askıdaki işlem çözülemedi",
    idle45s: "45 sn hareketsizlik",
    manual: "Kilit butonu"
  },
  navigation: { navigate: "İlk açılış", reload: "Sayfa yenilendi (F5)", back_forward: "Geri/ileri" },
  trigger: { manual: "Bağlan butonu", screenOpen: "Ekran açılışı" }
};

export default {
  components: { flatPickr, vSelect },
  data() {
    return {
      branches: [],
      personnels: [],
      selectedBranchId: null,
      selectedPersonalId: null,
      selectedEventType: null,
      filterClientSessionId: "",
      onlyProblems: false,
      dateFrom: null,
      dateTo: null,
      dateTimeConfig: {
        enableTime: true,
        time_24hr: true,
        dateFormat: "Y-m-d H:i",
        allowInput: true
      },
      eventTypeOptions: [
        { label: "POS Ödeme İsteği", value: 12 },
        { label: "POS Ödeme Onaylandı", value: 13 },
        { label: "POS Sonucu Alınamadı / Başarısız", value: 14 },
        { label: "POS Ödemesi Kasiyer Onayıyla", value: 15 },
        { label: "POS Bağlantısı Kurulamadı", value: 21 },
        { label: "Fiyat Doğrulaması Engellendi", value: 22 },
        { label: "Sipariş Kapatılamadı", value: 16 },
        { label: "Uygulama Hatası", value: 20 },
        { label: "Ekran Kilitlendi", value: 17 },
        { label: "Ekran Kilidi Açıldı", value: 18 },
        { label: "Aktif Personel Değişti", value: 9 },
        { label: "Kasa Ekranı Açıldı", value: 19 },
        { label: "Ödeme Ekranından Geri Dönme / Sepet Silme", value: 6 },
        { label: "Ürün Silindi", value: 2 },
        { label: "Sepet Kaldırıldı", value: 4 },
        { label: "İndirim Uygulandı", value: 11 },
        { label: "QR Ödeme Başlatıldı", value: 10 },
        { label: "Mobil Sipariş İptal", value: 7 },
        { label: "Entegrasyon Sipariş İptal", value: 8 }
      ],
      items: [],
      totalCount: 0,
      isTruncated: false,
      isLoading: false
    };
  },
  computed: {
    isSuperAdmin() {
      return this.$isSuperAdmin();
    },
    visibleItems() {
      if (!this.onlyProblems) return this.items;
      return this.items.filter((r) => this.isProblem(r));
    }
  },
  async mounted() {
    if (!this.isSuperAdmin) return;
    this.resetDates();
    await this.loadBranchList();
    await this.loadPersonalList();
    this.loadEvents();
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, "0");
    },
    toLocalInput(d) {
      return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())} ${this.pad(d.getHours())}:${this.pad(d.getMinutes())}`;
    },
    resetDates() {
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      this.dateFrom = this.toLocalInput(start);
      this.dateTo = this.toLocalInput(now);
    },
    resetFilters() {
      this.selectedBranchId = null;
      this.selectedPersonalId = null;
      this.selectedEventType = null;
      this.filterClientSessionId = "";
      this.onlyProblems = false;
      this.resetDates();
      this.loadPersonalList();
      this.loadEvents();
    },
    /** Kayıtlar sunucuda Türkiye yerel saatiyle (saat dilimsiz) tutulur; parametreyi de öyle gönderiyoruz. */
    toServerDate(v, endOfMinute) {
      if (!v) return null;
      return v.replace(" ", "T") + (endOfMinute ? ":59" : ":00");
    },
    async loadBranchList() {
      try {
        const res = await getBranchNameList();
        this.branches = res && res.status === 200 && Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.branches = [];
      }
    },
    async loadPersonalList() {
      try {
        const res = await getPersonalNameList(this.selectedBranchId);
        this.personnels = res && res.status === 200 && Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.personnels = [];
      }
    },
    onBranchChange() {
      this.selectedPersonalId = null;
      this.loadPersonalList();
    },
    filterBySession(sessionId) {
      this.filterClientSessionId = sessionId;
      this.loadEvents();
    },
    async loadEvents() {
      this.isLoading = true;
      try {
        const params = {
          from: this.toServerDate(this.dateFrom, false),
          to: this.toServerDate(this.dateTo, true)
        };
        if (this.selectedBranchId != null) params.branchId = this.selectedBranchId;
        if (this.selectedPersonalId != null) params.personalId = this.selectedPersonalId;
        if (this.selectedEventType != null) params.eventType = this.selectedEventType;
        if (this.filterClientSessionId) params.clientSessionId = this.filterClientSessionId.trim();
        const res = await getSelfServiceDiagnosticEvents(params);
        const data = res && res.data ? res.data : {};
        this.items = data.items || [];
        this.totalCount = data.totalCount || 0;
        this.isTruncated = !!data.isTruncated;
      } catch (e) {
        this.items = [];
        this.$vs.notify({ color: "danger", title: "Hata", text: "Teşhis günlüğü yüklenemedi." });
      } finally {
        this.isLoading = false;
      }
    },
    eventTime(row) {
      return row.clientTimestamp || row.created;
    },
    serverDelaySeconds(row) {
      if (!row.clientTimestamp || !row.created) return 0;
      return Math.round((new Date(row.created) - new Date(row.clientTimestamp)) / 1000);
    },
    formatDateTime(v) {
      if (!v) return "";
      return new Date(v).toLocaleString("tr-TR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit"
      });
    },
    parsePayload(row) {
      if (!row.payloadJson) return null;
      try {
        const p = JSON.parse(row.payloadJson);
        return p && typeof p === "object" ? p : null;
      } catch (e) {
        return null;
      }
    },
    formatValue(key, value) {
      if (value === true) return "Evet";
      if (value === false) return "Hayır";
      if (VALUE_LABELS[key] && VALUE_LABELS[key][value]) return VALUE_LABELS[key][value];
      if (key === "durationMs") return (Number(value) / 1000).toFixed(1) + " sn";
      if (["amount", "cartTotal", "paidTotal", "totalPrice"].includes(key) && !isNaN(Number(value))) {
        return Number(value).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " ₺";
      }
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    },
    detailLines(row) {
      const p = this.parsePayload(row);
      // Sepet/indirim gibi rapor olayları için sunucunun hazırladığı Türkçe satırlar daha zengin.
      if (!p || !this.isDiagnosticType(row.eventType)) {
        return row.payloadLines && row.payloadLines.length ? row.payloadLines : [];
      }
      return Object.keys(p)
        .filter((k) => p[k] !== null && p[k] !== undefined && p[k] !== "" && k !== "userAgent" && k !== "personalId" && k !== "paymentMethodId")
        .map((k) => `${PAYLOAD_LABELS[k] || k}: ${this.formatValue(k, p[k])}`);
    },
    isDiagnosticType(t) {
      return [
        "PosSaleRequested", "PosSaleSucceeded", "PosSaleFailed", "PosPaymentManuallyConfirmed",
        "OrderCloseFailed", "ScreenLocked", "ScreenUnlocked", "SessionStarted", "ClientError",
        "PosConnectFailed", "ActivePersonalChanged", "PriceValidationBlocked"
      ].includes(t);
    },
    prettyJson(json) {
      try {
        return JSON.stringify(JSON.parse(json), null, 2);
      } catch (e) {
        return json;
      }
    },
    isProblem(row) {
      if (!PROBLEM_TYPES.includes(row.eventType)) return false;
      if (row.eventType === "PosSaleFailed") {
        const p = this.parsePayload(row) || {};
        return !NON_PROBLEM_POS_OUTCOMES.includes(p.outcome);
      }
      return true;
    },
    /** "Ödeme akışı iptal" kaydını detayına göre anlaşılır adla göster (backend adı genel). */
    eventLabel(row) {
      if (row.eventType === "PaymentFlowCancelled") {
        const p = this.parsePayload(row) || {};
        return p.Durum ? "İndirimli/ödemeli sepet silindi" : "Ödeme ekranından geri dönüldü";
      }
      return row.eventTypeDisplay || row.eventType;
    },
    rowClass(row) {
      return this.isProblem(row) ? "diag-row--problem" : "";
    },
    badgeClass(row) {
      const t = row.eventType;
      if (t === "PosSaleSucceeded" || t === "ScreenUnlocked") return "diag-badge--success";
      if (t === "PosPaymentManuallyConfirmed") return "diag-badge--warn";
      if (t === "PaymentFlowCancelled") return (this.parsePayload(row) || {}).Durum ? "diag-badge--warn" : "diag-badge--neutral";
      if (t === "PosSaleFailed" && !this.isProblem(row)) return "diag-badge--warn";
      if (PROBLEM_TYPES.includes(t)) return "diag-badge--danger";
      if (t === "PosSaleRequested" || t === "SessionStarted") return "diag-badge--info";
      return "diag-badge--neutral";
    }
  }
};
</script>

<style scoped>
.diag-table-wrap {
  overflow-x: auto;
}
.diag-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.diag-table th,
.diag-table td {
  padding: 6px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  text-align: left;
  vertical-align: top;
}
.diag-table th {
  font-weight: 600;
  white-space: nowrap;
  background: rgba(0, 0, 0, 0.03);
}
.nowrap {
  white-space: nowrap;
}
.diag-detail {
  min-width: 280px;
  max-width: 520px;
  word-break: break-word;
}
.diag-row--problem {
  background: rgba(234, 84, 85, 0.06);
}
.diag-raw {
  white-space: pre-wrap;
  font-size: 0.75rem;
  max-height: 240px;
  overflow: auto;
  background: rgba(0, 0, 0, 0.04);
  padding: 6px;
  border-radius: 4px;
}
.diag-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.diag-badge--success { background: rgba(40, 199, 111, 0.15); color: #1f9d57; }
.diag-badge--danger { background: rgba(234, 84, 85, 0.15); color: #c43c3d; }
.diag-badge--warn { background: rgba(255, 159, 67, 0.18); color: #c46a12; }
.diag-badge--info { background: rgba(0, 207, 232, 0.15); color: #0a8fa1; }
.diag-badge--neutral { background: rgba(0, 0, 0, 0.06); color: #555; }
</style>
